// =============================================================================
// /auth/callback – Dedicated route for Supabase email verification (and invite-after-confirm).
// Supabase redirects here with ?code=... (PKCE) or #access_token=...&refresh_token=... (hash).
// Invite flow runs ONLY when invite_token is in the URL (not from localStorage).
// After session is established and no invite in URL, redirect to / for normal routing.
// Add https://portal.stravo.ai/auth/callback to Supabase Auth → URL Configuration → Redirect URLs.
// =============================================================================

import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LogOut } from "lucide-react";
import { storeInviteToken, clearInviteToken, isLiveInvite } from "@/lib/inviteTokenStorage";
import { setEmailVerifiedForUser } from "@/lib/emailVerification";
import { setViewContextFromInvite } from "@/contexts/ViewContext";
import { useToast } from "@/hooks/use-toast";
import { hasSupabaseAuthCallbackParams } from "@/lib/supabaseAuthCallback";

interface InviteDetails {
  id: string;
  email: string;
  invite_role: string;
  property_id: string | null;
  property_name: string | null;
  inviter_name: string | null;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  access_level: string | null;
}

export default function AuthCallbackPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, profile, loading: authLoading, signOut, refetchProfile } = useAuth();
  const { toast } = useToast();
  // Only treat as invite flow when invite_token is in the URL (avoids email verification being mis-routed as invite)
  const inviteTokenFromUrl = searchParams.get("invite_token")?.trim() ?? null;
  const inviteToken = inviteTokenFromUrl;
  const code = searchParams.get("code");
  const authErrorParam = searchParams.get("error");
  const authErrorDescription = searchParams.get("error_description");
  const [hashProcessed, setHashProcessed] = useState(false);

  const [inviteDetails, setInviteDetails] = useState<InviteDetails | null>(null);
  const [inviteLoading, setInviteLoading] = useState(!!inviteToken);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [codeExchanged, setCodeExchanged] = useState(false);
  const done = useRef(false);
  const acceptStarted = useRef(false);
  const [showFallback, setShowFallback] = useState(false);

  // Fallback: empty callback (logout bounce) or failed PKCE should not spin forever
  useEffect(() => {
    if (inviteToken !== null) return;
    if (authErrorParam) {
      setShowFallback(true);
      return;
    }
    const hasAuthPayload = hasSupabaseAuthCallbackParams();
    if (!hasAuthPayload) {
      if (!authLoading && !user) {
        setShowFallback(true);
      }
      return;
    }
    const timer = setTimeout(() => {
      if (done.current) return;
      if (!user) setShowFallback(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, [inviteToken, code, user, authLoading, authErrorParam]);

  // Log params on mount (no PII)
  useEffect(() => {
    const hasCode = !!code;
    const hasInviteInUrl = !!inviteTokenFromUrl;
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    const hasHash = hash.length > 1;
    console.log("[AuthCallback] Params received", {
      hasCode,
      hasInviteInUrl,
      hasHash,
      hashKeys: hasHash ? Array.from(new URLSearchParams(hash.slice(1)).keys()) : [],
    });
  }, [code, inviteTokenFromUrl]);

  // PKCE: exchange code for session if present. detectSessionInUrl may already have consumed it.
  useEffect(() => {
    if (!code || codeExchanged) return;
    let cancelled = false;
    void (async () => {
      const { data: existing } = await supabase.auth.getSession();
      if (cancelled) return;
      if (existing.session) {
        setCodeExchanged(true);
        console.log("[AuthCallback] Session already present; skipping code exchange");
        return;
      }
      const { data, error } = await supabase.auth.exchangeCodeForSession(code);
      if (cancelled) return;
      if (error || !data.session) {
        console.error("[AuthCallback] exchangeCodeForSession error", error);
        setCodeExchanged(true);
        setShowFallback(true);
        return;
      }
      setCodeExchanged(true);
      console.log("[AuthCallback] Session exchange (code) succeeded");
    })();
    return () => {
      cancelled = true;
    };
  }, [code, codeExchanged]);

  // Hash flow: Supabase may redirect with #access_token=...&refresh_token=... (e.g. type=email)
  useEffect(() => {
    if (hashProcessed) return;
    const hash = typeof window === "undefined" ? "" : window.location.hash;
    if (!hash || hash.length < 2) return;
    const hashParams = new URLSearchParams(hash.slice(1));
    const accessToken = hashParams.get("access_token");
    const refreshToken = hashParams.get("refresh_token");
    if (!accessToken) return;
    let cancelled = false;
    supabase.auth
      .setSession({ access_token: accessToken, refresh_token: refreshToken ?? "" })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data.session) {
          console.error("[AuthCallback] setSession from hash error", error);
          setHashProcessed(true);
          setShowFallback(true);
          return;
        }
        setHashProcessed(true);
        console.log("[AuthCallback] Session set from hash");
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("[AuthCallback] setSession from hash error", err);
          setHashProcessed(true);
          setShowFallback(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [hashProcessed]);

  // Fetch invite details only when we have invite_token in URL
  useEffect(() => {
    if (!inviteToken) {
      setInviteLoading(false);
      return;
    }
    setInviteLoading(true);
    setInviteError(null);
    void (async () => {
      const fail = (message: string) => {
        clearInviteToken();
        setInviteError(message);
        setInviteLoading(false);
      };
      const member = await supabase.rpc("get_member_invite_by_token", { p_token: inviteToken });
      if (!member.error && member.data) {
        const inv = member.data as InviteDetails;
        if (!isLiveInvite(inv)) {
          fail(
            inv.revoked_at
              ? "This invite has been revoked."
              : inv.accepted_at
                ? "This invite has already been used."
                : "This invite has expired. Ask the sender to resend.",
          );
          return;
        }
        storeInviteToken(inviteToken);
        setInviteDetails(inv);
        setInviteLoading(false);
        return;
      }

      const cohost = await supabase.rpc("get_cohost_invite_by_token", { p_token: inviteToken });
      if (cohost.error || !cohost.data) {
        console.log("[AuthCallback] Invite fetch: invalid or error", {
          member: member.error?.message,
          cohost: cohost.error?.message,
        });
        fail("Invalid or expired invite. Ask the sender to resend.");
        return;
      }
      const raw = cohost.data as Record<string, unknown>;
      const inv: InviteDetails = {
        id: String(raw.id ?? ""),
        email: String(raw.email ?? raw.invited_email ?? ""),
        invite_role: "cohost",
        property_id: (raw.property_id as string) || null,
        property_name: (raw.property_name as string) || null,
        inviter_name: (raw.inviter_name as string) || null,
        expires_at: String(raw.expires_at ?? ""),
        accepted_at: (raw.accepted_at as string) || null,
        revoked_at: (raw.revoked_at as string) || null,
        access_level: (raw.access_level as string) || null,
      };
      if (!isLiveInvite(inv)) {
        fail(
          inv.revoked_at
            ? "This invite has been revoked."
            : inv.accepted_at
              ? "This invite has already been used."
              : "This invite has expired. Ask the sender to resend.",
        );
        return;
      }
      storeInviteToken(inviteToken);
      setInviteDetails(inv);
      setInviteLoading(false);
    })();
  }, [inviteToken]);

  // No invite in URL: after session is in context (user set), redirect to Index so they land in app not landing page.
  // Clear ?code= and #hash from URL before navigating so Index doesn't redirect back here (prevents loop).
  // If user is already set (detectSessionInUrl), do not wait on a second code exchange.
  useEffect(() => {
    if (inviteToken !== null) return;
    if (authErrorParam) return;
    if (authLoading) return;
    if (code && !codeExchanged && !user) return;
    if (!user) return; // wait for session to be in context so Index sees logged-in user and routes to dashboard
    if (done.current) return;
    done.current = true;
    console.log("[AuthCallback] No invite in URL; redirecting to /");
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    window.history.replaceState(null, "", origin + "/");
    navigate("/", { replace: true });
  }, [inviteToken, authLoading, code, codeExchanged, user, navigate, authErrorParam]);

  // After session is ready and invite is valid: accept and redirect (or show Different account)
  useEffect(() => {
    if (done.current) return;
    if (authLoading || !user || !profile) return;
    // If PKCE: wait for code exchange; if no code (hash flow), proceed
    if (code && !codeExchanged && !user) return;
    if (!inviteToken || !inviteDetails) return;
    if (inviteDetails.accepted_at) {
      done.current = true;
      queryClient.invalidateQueries({ queryKey: ["company"] });
      clearInviteToken();
      setSearchParams({}, { replace: true });
      routeByRole(inviteDetails.invite_role, inviteDetails.property_id);
      return;
    }

    const userEmail = user.email?.toLowerCase().trim();
    const inviteEmail = (inviteDetails.email ?? "").toLowerCase();
    if (userEmail !== inviteEmail) {
      // Show "Different account" below; do not accept or redirect
      return;
    }

    if (acceptStarted.current) return;
    acceptStarted.current = true;
    setAccepting(true);
    const acceptRpc =
      inviteDetails.invite_role === "cohost"
        ? supabase.rpc("accept_cohost_invite", { p_token: inviteToken.trim() })
        : supabase.rpc("accept_member_invite", { p_token: inviteToken.trim() });
    void acceptRpc.then(async ({ error }) => {
        if (error) {
          const alreadyAccepted = /already accepted|not found.*accepted/i.test(error.message);
          if (alreadyAccepted && profile?.role === "company_admin") {
            // Invite was already accepted (e.g. previous attempt); still redirect so they get to dashboard
            await refetchProfile();
            await queryClient.invalidateQueries({ queryKey: ["company"] });
            await queryClient.invalidateQueries({ queryKey: ["company-properties"] });
            done.current = true;
            setAccepting(false);
            clearInviteToken();
            setSearchParams({}, { replace: true });
            routeByRole(inviteDetails.invite_role, inviteDetails.property_id);
            return;
          }
          setAccepting(false);
          toast({ title: "Could not accept invite", description: error.message, variant: "destructive" });
          setInviteError(error.message);
          return;
        }
        // Cleaner invites: mark email as verified so they never see the verify-email page
        if (inviteDetails.invite_role === "cleaner") {
          await setEmailVerifiedForUser(user.id);
        }
        await refetchProfile();
        await queryClient.invalidateQueries({ queryKey: ["company"] });
        await queryClient.invalidateQueries({ queryKey: ["access-resolver"] });
        await queryClient.invalidateQueries({ queryKey: ["user-capabilities"] });
        await queryClient.invalidateQueries({ queryKey: ["cleaner"] });
        done.current = true;
        setAccepting(false);
        clearInviteToken();
        setSearchParams({}, { replace: true });
        routeByRole(inviteDetails.invite_role, inviteDetails.property_id);
      })
      .catch((err) => {
        setAccepting(false);
        toast({ title: "Error", description: "Could not accept invite.", variant: "destructive" });
        setInviteError(err instanceof Error ? err.message : "Something went wrong.");
      });
  }, [authLoading, user, profile, codeExchanged, code, inviteToken, inviteDetails, refetchProfile, queryClient, setSearchParams, toast]);

  function routeByRole(role: string, propertyId: string | null) {
    const q = propertyId ? `?property_id=${encodeURIComponent(propertyId)}` : "";
    switch (role) {
      case "company_admin":
      case "cohost":
        setViewContextFromInvite("owner");
        navigate(`/owner/properties${q}`, { replace: true });
        break;
      case "cleaner":
        setViewContextFromInvite("cleaner");
        navigate(`/worker/schedule${q}`, { replace: true });
        break;
      default:
        setViewContextFromInvite("owner");
        navigate("/owner/properties", { replace: true });
    }
  }

  const loginUrl = inviteToken ? `/login?invite_token=${encodeURIComponent(inviteToken)}` : "/login";
  const handleSwitchAccount = async () => {
    await signOut();
    navigate(loginUrl, { replace: true });
  };

  // Different account: only "Switch account to accept"
  if (user && inviteDetails && !inviteDetails.accepted_at) {
    const userEmail = user.email?.toLowerCase().trim();
    const inviteEmail = (inviteDetails.email ?? "").toLowerCase();
    if (userEmail !== inviteEmail) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Different account</CardTitle>
              <CardDescription>
                You're signed in as <span className="font-medium text-foreground">{user.email}</span>, but this invite is for{" "}
                <span className="font-medium text-foreground">{inviteDetails.email}</span>. Sign in with the invited email to accept.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={handleSwitchAccount} className="w-full" size="lg">
                <LogOut className="h-4 w-4 mr-2" />
                Switch account to accept
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
  }

  // Invite error (invalid/expired/revoked)
  if (inviteError && !inviteDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invite problem</CardTitle>
            <CardDescription>{inviteError}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => {
                clearInviteToken();
                navigate("/login", { replace: true });
              }}
              className="w-full"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Fallback: no code/hash in URL, or verification taking too long – offer sign in so user isn't stuck
  if (showFallback && !inviteToken && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Sign in to continue</CardTitle>
            <CardDescription>
              {authErrorDescription
                ? authErrorDescription.replace(/\+/g, " ")
                : "This link may have already been used, or the session ended on another device. Sign in with your email and password to open the app."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/login", { replace: true })} className="w-full" size="lg">
              Go to sign in
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading: waiting for session and/or invite
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6 flex flex-col items-center gap-4">
          <p className="text-sm text-muted-foreground">
            {accepting ? "Accepting invite…" : "Loading…"}
          </p>
          {inviteError && inviteDetails && (
            <p className="text-sm text-destructive">{inviteError}</p>
          )}
          {!inviteToken && (
            <Button
              type="button"
              variant="ghost"
              className="text-muted-foreground"
              onClick={() => navigate("/login", { replace: true })}
            >
              Go to sign in
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
